import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from '@tanstack/react-router'
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { Shell } from './components/Shell'
import { StepUp } from './components/StepUp'
import { Spinner } from './components/ui'
import { ApiError, api } from './lib/api'
import { I18nProvider, initialLocale, useT } from './lib/i18n'
import { useMe } from './lib/session'
import { CommissionsPage } from './pages/owner/Commissions'
import { OnboardingPage } from './pages/owner/Onboarding'
import { OverviewPage } from './pages/owner/Overview'
import { PartnersPage } from './pages/owner/Partners'
import { FraudPage, PayoutsPage } from './pages/owner/Payouts'
import { SettingsPage } from './pages/owner/Settings'
import {
  ConfirmPayoutPage,
  PartnerEarningsPage,
  PartnerOverviewPage,
  PartnerPayoutsPage,
  PartnerProgramPage,
} from './pages/partner/Partner'
import { JoinPage } from './pages/public/Join'
import { LoginPage } from './pages/public/Login'
import { MfaPage } from './pages/public/Mfa'
import { OAuthConsentPage } from './pages/public/OAuthConsent'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
      refetchOnWindowFocus: false,
    },
  },
})

function RequireUser({ children }: { children: React.ReactNode }) {
  const me = useMe()
  if (me.isLoading) return <Spinner />
  if (!me.data?.user)
    return <Navigate to="/login" search={{ next: location.pathname + location.search } as never} />
  return <>{children}</>
}

function OwnerLayout() {
  const { t } = useT()
  const me = useMe()
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: () => api<{ portal: { slug: string } | null }>('/api/config'),
  })
  if (me.isLoading || !me.data || cfg.isLoading) return <Spinner />
  // a client's own partner-portal domain shows only the partner area (plan, 7.1)
  if (cfg.data?.portal)
    return me.data.user ? (
      <Navigate to="/partner" />
    ) : (
      <Navigate to={`/join/${cfg.data.portal.slug}` as '/'} />
    )
  if (!me.data.user) return <Navigate to="/login" />
  if (!me.data.memberships.length)
    return me.data.partner ? <Navigate to="/partner" /> : <Navigate to="/onboarding" />
  if (me.data.mfa?.required && me.data.mfa.aal !== 'aal2') {
    return <Navigate to="/mfa" search={{ next: location.pathname + location.search } as never} />
  }
  return (
    <Shell
      me={me.data}
      area="owner"
      nav={[
        { to: '/', label: t('overview'), icon: 'overview', exact: true },
        { to: '/partners', label: t('partners'), icon: 'partners' },
        { to: '/commissions', label: t('commissions'), icon: 'commissions' },
        { to: '/payouts', label: t('payouts'), icon: 'payouts' },
        { to: '/fraud', label: t('fraud'), icon: 'fraud' },
        { to: '/settings', label: t('settings'), icon: 'settings' },
      ]}
    />
  )
}

function PartnerLayout() {
  const { t } = useT()
  const me = useMe()
  const qc = useQueryClient()
  // an applicant or invited partner signs in with the same e-mail: link the profile right away
  const link = useMutation({
    mutationFn: () => api('/api/partner/profile', { body: {} }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })
  const needsProfile = !!me.data?.user && !me.data.partner
  useEffect(() => {
    if (needsProfile && link.isIdle) link.mutate()
  }, [needsProfile, link])
  if (me.isLoading || !me.data) return <Spinner />
  if (!me.data.user)
    return <Navigate to="/login" search={{ next: location.pathname + location.search } as never} />
  if (needsProfile && !link.isError) return <Spinner />
  return (
    <Shell
      me={me.data}
      area="partner"
      nav={[
        { to: '/partner', label: t('overview'), icon: 'overview', exact: true },
        { to: '/partner/earnings', label: t('earnings'), icon: 'earnings' },
        { to: '/partner/payouts', label: t('payouts'), icon: 'payouts' },
      ]}
    />
  )
}

const root = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <StepUp />
    </>
  ),
})
const login = createRoute({ getParentRoute: () => root, path: '/login', component: LoginPage })
const mfa = createRoute({
  getParentRoute: () => root,
  path: '/mfa',
  component: () => (
    <RequireUser>
      <MfaPage />
    </RequireUser>
  ),
})
const join = createRoute({ getParentRoute: () => root, path: '/join/$slug', component: JoinPage })
const oauthConsent = createRoute({
  getParentRoute: () => root,
  path: '/oauth/consent',
  component: () => (
    <RequireUser>
      <OAuthConsentPage />
    </RequireUser>
  ),
})
const onboarding = createRoute({
  getParentRoute: () => root,
  path: '/onboarding',
  component: () => (
    <RequireUser>
      <OnboardingPage />
    </RequireUser>
  ),
})
const owner = createRoute({ getParentRoute: () => root, id: 'owner', component: OwnerLayout })
const partnerArea = createRoute({ getParentRoute: () => root, path: '/partner', component: PartnerLayout })

const routeTree = root.addChildren([
  login,
  mfa,
  join,
  oauthConsent,
  onboarding,
  owner.addChildren([
    createRoute({ getParentRoute: () => owner, path: '/', component: OverviewPage }),
    createRoute({ getParentRoute: () => owner, path: '/partners', component: PartnersPage }),
    createRoute({ getParentRoute: () => owner, path: '/commissions', component: CommissionsPage }),
    createRoute({ getParentRoute: () => owner, path: '/payouts', component: PayoutsPage }),
    createRoute({ getParentRoute: () => owner, path: '/fraud', component: FraudPage }),
    createRoute({ getParentRoute: () => owner, path: '/settings', component: SettingsPage }),
  ]),
  partnerArea.addChildren([
    createRoute({ getParentRoute: () => partnerArea, path: '/', component: PartnerOverviewPage }),
    createRoute({ getParentRoute: () => partnerArea, path: '/programs/$id', component: PartnerProgramPage }),
    createRoute({ getParentRoute: () => partnerArea, path: '/earnings', component: PartnerEarningsPage }),
    createRoute({ getParentRoute: () => partnerArea, path: '/payouts', component: PartnerPayoutsPage }),
    createRoute({ getParentRoute: () => partnerArea, path: '/confirm-payout', component: ConfirmPayoutPage }),
  ]),
])

const router = createRouter({ routeTree, defaultPreload: 'intent' })
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const locale = await initialLocale()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider locale={locale}>
        <RouterProvider router={router} />
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
)
